import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Snackbar,
    Alert,
    TextField,
    InputAdornment,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    ToggleButtonGroup,
    ToggleButton,
    Card,
    CardContent,
    Divider,
    Grid,
    Avatar,
    alpha,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    Edit as EditIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    LocalTaxi as TaxiIcon,
    Groups as GroupsIcon,
    Category as CategoryIcon,
    CheckCircle as CheckCircleIcon,
    FilterAlt as FilterAltIcon,
} from '@mui/icons-material';

import TaxiServicesTypeForm from './TaxiServicesTypeForm';
import { useTaxiServicesTypes } from '../../../hooks/administration/useTaxiServicesTypes';
import { useAuthContext } from '../../../context/AuthProvider';

const TaxiServicesTypesDashboard = () => {
    const [open, setOpen] = useState(false);
    const [editTaxiTypeService, setEditTaxiTypeService] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedTaxiTypeService, setSelectedTaxiTypeService] = useState(null);

    const { user } = useAuthContext();

    const {
        taxiServicesTypes = [],
        isLoading,
        createMutation,
        updateMutation,
        deleteMutation,
    } = useTaxiServicesTypes();

    const isMutating =
        createMutation.isPending ||
        updateMutation.isPending ||
        deleteMutation.isPending;

    const showAlert = useCallback((message, severity = 'success') => {
        setAlert({ open: true, message, severity });
    }, []);

    const handleOpenCreate = useCallback(() => {
        setEditTaxiTypeService(null);
        setOpen(true);
    }, []);

    const handleOpenEdit = useCallback((row) => {
        setEditTaxiTypeService(row);
        setOpen(true);
    }, []);

    const handleCloseForm = useCallback(() => {
        if (createMutation.isPending || updateMutation.isPending) return;
        setOpen(false);
        setEditTaxiTypeService(null);
    }, [createMutation.isPending, updateMutation.isPending]);

    const handleOpenConfirm = useCallback((row) => {
        setSelectedTaxiTypeService(row);
        setOpenConfirm(true);
    }, []);

    const handleCloseConfirm = useCallback(() => {
        if (deleteMutation.isPending) return;
        setOpenConfirm(false);
        setSelectedTaxiTypeService(null);
    }, [deleteMutation.isPending]);

    const handleSubmit = useCallback(
        (data) => {
            if (!user?.EmpID) {
                showAlert('Debe iniciar sesión para realizar esta acción.', 'error');
                return;
            }

            const payload = {
                taxtipnom: data.taxtipnom?.trim(),
                taxtipcan: Number(data.taxtipcan),
                taxtipdes: data.taxtipdes?.trim() || null,
            };

            if (editTaxiTypeService?.taxtipid) {
                updateMutation.mutate(
                    {
                        id: editTaxiTypeService.taxtipid,
                        data: {
                            ...payload,
                            updbyid: user.EmpID,
                        },
                    },
                    {
                        onSuccess: () => {
                            showAlert('Tipo de servicio taxi actualizado correctamente.');
                            setOpen(false);
                            setEditTaxiTypeService(null);
                        },
                        onError: (err) => {
                            showAlert(
                                err?.response?.data?.error ||
                                err?.message ||
                                'Error al actualizar el tipo de servicio taxi.',
                                'error'
                            );
                        },
                    }
                );

                return;
            }

            createMutation.mutate(
                {
                    ...payload,
                    crebyid: user.EmpID,
                },
                {
                    onSuccess: () => {
                        showAlert('Tipo de servicio taxi creado correctamente.');
                        setOpen(false);
                    },
                    onError: (err) => {
                        showAlert(
                            err?.response?.data?.error ||
                            err?.message ||
                            'Error al crear el tipo de servicio taxi.',
                            'error'
                        );
                    },
                }
            );
        },
        [user, editTaxiTypeService, createMutation, updateMutation, showAlert]
    );

    const handleConfirmDelete = useCallback(() => {
        if (!selectedTaxiTypeService?.taxtipid) return;

        deleteMutation.mutate(selectedTaxiTypeService.taxtipid, {
            onSuccess: () => {
                showAlert('Tipo de servicio taxi eliminado correctamente.');
                setOpenConfirm(false);
                setSelectedTaxiTypeService(null);
            },
            onError: (err) => {
                showAlert(
                    err?.response?.data?.error ||
                    err?.message ||
                    'Error al eliminar el tipo de servicio taxi.',
                    'error'
                );
            },
        });
    }, [selectedTaxiTypeService, deleteMutation, showAlert]);

    const rows = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        return taxiServicesTypes
            .filter((item) => {
                if (statusFilter === 'A') return item.estreg === 'A';
                if (statusFilter === 'NOT_A') return item.estreg !== 'A';
                return true;
            })
            .filter((item) => {
                if (!normalizedSearch) return true;

                return (
                    (item.taxtipnom || '').toLowerCase().includes(normalizedSearch) ||
                    (item.taxtipdes || '').toLowerCase().includes(normalizedSearch)
                );
            });
    }, [taxiServicesTypes, search, statusFilter]);

    const totalActive = useMemo(
        () => taxiServicesTypes.filter((item) => item.estreg === 'A').length,
        [taxiServicesTypes]
    );

    const columns = useMemo(
        () => [
            {
                field: 'taxtipnom',
                headerName: 'Tipo de servicio',
                flex: 1,
                minWidth: 240,
                renderCell: (params) => (
                    <Stack direction="row" spacing={1.2} alignItems="center">
                        <TaxiIcon color="primary" fontSize="small" />
                        <Typography fontWeight={600} noWrap>
                            {params.row?.taxtipnom || '—'}
                        </Typography>
                    </Stack>
                ),
            },
            {
                field: 'taxtipcan',
                headerName: 'Pasajeros por grupo',
                width: 190,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => (
                    <Chip
                        icon={<GroupsIcon />}
                        label={`${params.row?.taxtipcan ?? 0} pasajeros`}
                        size="small"
                        variant="outlined"
                    />
                ),
            },
            {
                field: 'taxtipdes',
                headerName: 'Descripción',
                flex: 1.4,
                minWidth: 300,
                renderCell: (params) => (
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {params.row?.taxtipdes || 'Sin descripción'}
                    </Typography>
                ),
            },
            {
                field: 'estreg',
                headerName: 'Estado',
                width: 130,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const isActive = params.value === 'A';

                    return (
                        <Chip
                            label={isActive ? 'Activo' : 'Inactivo'}
                            color={isActive ? 'success' : 'warning'}
                            size="small"
                            variant="outlined"
                        />
                    );
                },
            },
            {
                field: 'actions',
                headerName: 'Acciones',
                width: 120,
                sortable: false,
                filterable: false,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => (
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Editar">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpenEdit(params.row)}
                                    disabled={isMutating}
                                >
                                    <EditIcon color="primary" fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="Eliminar">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpenConfirm(params.row)}
                                    disabled={isMutating}
                                >
                                    <DeleteIcon color="error" fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        [handleOpenEdit, handleOpenConfirm, isMutating]
    );

    return (
        <Box sx={{ bgcolor: '#f4f6f8', minHeight: '100vh', p: 2.5 }}>
            <Grid container spacing={2.5}>
                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        sx={{
                            px: 3,
                            py: 2.5,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar
                                        sx={{
                                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                                            color: 'primary.main',
                                        }}
                                    >
                                        <TaxiIcon />
                                    </Avatar>

                                    <Box>
                                        <Typography variant="h5" fontWeight={800}>
                                            Tipos de servicio taxi
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                            Administra los tipos de servicio y la cantidad máxima de pasajeros por grupo.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Grid>

                            <Grid item xs={12} md="auto">
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenCreate}
                                    disableElevation
                                    disabled={isMutating}
                                    sx={{ minWidth: 160 }}
                                >
                                    Nuevo tipo
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card
                        elevation={0}
                        sx={{
                            height: '100%',
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Total registrados
                                    </Typography>
                                    <Typography variant="h4" fontWeight={800}>
                                        {taxiServicesTypes.length}
                                    </Typography>
                                </Box>

                                <Avatar
                                    sx={{
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                                        color: 'primary.main',
                                        width: 48,
                                        height: 48,
                                    }}
                                >
                                    <CategoryIcon />
                                </Avatar>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card
                        elevation={0}
                        sx={{
                            height: '100%',
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Activos
                                    </Typography>
                                    <Typography variant="h4" fontWeight={800} color="success.main">
                                        {totalActive}
                                    </Typography>
                                </Box>

                                <Avatar
                                    sx={{
                                        bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                                        color: 'success.main',
                                        width: 48,
                                        height: 48,
                                    }}
                                >
                                    <CheckCircleIcon />
                                </Avatar>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card
                        elevation={0}
                        sx={{
                            height: '100%',
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Mostrados
                                    </Typography>
                                    <Typography variant="h4" fontWeight={800}>
                                        {rows.length}
                                    </Typography>
                                </Box>

                                <Avatar
                                    sx={{
                                        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                                        color: 'warning.main',
                                        width: 48,
                                        height: 48,
                                    }}
                                >
                                    <FilterAltIcon />
                                </Avatar>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ px: 3, py: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Buscar por nombre o descripción..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: search ? (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Limpiar">
                                                        <IconButton size="small" onClick={() => setSearch('')}>
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </InputAdornment>
                                            ) : null,
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md="auto">
                                    <ToggleButtonGroup
                                        fullWidth
                                        size="small"
                                        exclusive
                                        value={statusFilter}
                                        onChange={(_, value) => value && setStatusFilter(value)}
                                    >
                                        <ToggleButton value="ALL">Todos</ToggleButton>
                                        <ToggleButton value="A">Activos</ToggleButton>
                                        <ToggleButton value="NOT_A">Inactivos</ToggleButton>
                                    </ToggleButtonGroup>
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider />

                        <Box sx={{ height: 560, width: '100%' }}>
                            <DataGrid
                                rows={rows}
                                columns={columns}
                                loading={isLoading}
                                getRowId={(row) => row.taxtipid}
                                disableRowSelectionOnClick
                                pageSizeOptions={[10, 25, 50, 100]}
                                initialState={{
                                    pagination: {
                                        paginationModel: { pageSize: 10, page: 0 },
                                    },
                                    sorting: {
                                        sortModel: [{ field: 'taxtipnom', sort: 'asc' }],
                                    },
                                }}
                                sx={{
                                    border: 0,
                                    '& .MuiDataGrid-columnHeaders': {
                                        bgcolor: '#f8fafc',
                                        fontWeight: 800,
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        bgcolor: '#f9fbff',
                                    },
                                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
                                        outline: 'none',
                                    },
                                }}
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {open && (
                <TaxiServicesTypeForm
                    open={open}
                    onClose={handleCloseForm}
                    onSubmit={handleSubmit}
                    defaultValues={editTaxiTypeService || {}}
                    loading={createMutation.isPending || updateMutation.isPending}
                />
            )}

            <Dialog open={openConfirm} onClose={handleCloseConfirm} maxWidth="xs" fullWidth>
                <DialogTitle fontWeight={800}>Confirmar eliminación</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea eliminar el tipo de servicio taxi{' '}
                        <strong>{selectedTaxiTypeService?.taxtipnom}</strong>?
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseConfirm} color="inherit" disabled={deleteMutation.isPending}>
                        Cancelar
                    </Button>

                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleteMutation.isPending}
                        disableElevation
                    >
                        {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={alert.open}
                autoHideDuration={4000}
                onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
                    severity={alert.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TaxiServicesTypesDashboard;